/**
 * `Campanha · Primeiros dados` (Figma `149:1469`): what a campaign shows
 * before its collection has produced anything. That is a blocked state, not an
 * empty one, so the collection is working and nothing here names a time.
 */

import { Check, ChevronRight, CircleDashed, Clock, Newspaper, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';

import { getLocale } from '../../../i18n';
import { Absent } from '../../../chrome/measure';
import { count } from '../../../chrome/measure/format';
import { Page, StatCard, StatGrid } from '../../../chrome/page';
import { EmptyState } from '../../../chrome/state';
import { Button } from '../../../ui/button';
import { Card, CardContent, CardHeader } from '../../../ui/card';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from '../../../ui/item';
import { Skeleton } from '../../../ui/skeleton';
import { cn } from '../../../ui/utils';
import { cataloguePath, accountsPath } from '../../control/paths';
import { usersPath } from '../../tenant/paths';
import { fd, fdPlural } from './messages';

/** A value still on its way, or one the route refused to give. */
export type Pending<T> = T | 'pending' | 'failed';

export interface AccountsSummary {
  accounts: number;
  /** Distinct shared profiles the accounts use; an account with its own setup
   *  belongs to none. */
  profiles: number;
  /** The shared profile's own name, when exactly one is in use; `null` when
   *  zero or several are. */
  sharedProfileName: string | null;
}

export interface FirstDataProps {
  campaignId: string;
  tenantId: string;
  campaignName: string;
  accounts: Pending<AccountsSummary>;
  /** `null` when nothing is enabled, so there is nothing to price. */
  creditsPerDay: Pending<number | null>;
  /** Adding accounts and inviting people is a write; a read-only member is
   *  not offered what the routes would refuse. */
  canWrite: boolean;
}

function Loading(props: { className: string }): ReactNode {
  return <Skeleton role="status" aria-label={fd('loading')} className={props.className} />;
}

/** `done` fills the icon as a solid circle; the step still waiting keeps its
 *  icon plain and carries the accent instead on the card's own border, so it
 *  reads as the one collection is working on next. */
function Step(props: {
  icon: LucideIcon;
  title: string;
  subtitle: ReactNode;
  done: boolean;
}): ReactNode {
  const Icon = props.icon;
  return (
    <li>
      <Item variant="outline" className={props.done ? undefined : 'border-primary'}>
        <ItemMedia
          variant="icon"
          className={cn(
            'rounded-full',
            props.done && 'border-transparent bg-primary text-primary-foreground',
          )}
        >
          <Icon aria-hidden />
        </ItemMedia>
        <ItemContent>
          <ItemTitle>{props.title}</ItemTitle>
          <ItemDescription>{props.subtitle}</ItemDescription>
        </ItemContent>
      </Item>
    </li>
  );
}

function AccountsStep(props: { accounts: Pending<AccountsSummary> }): ReactNode {
  const { accounts } = props;
  const title = fd('step.accounts.title');
  if (accounts === 'pending') {
    return (
      <Step
        icon={CircleDashed}
        title={title}
        subtitle={<Loading className="h-4 w-24" />}
        done={false}
      />
    );
  }
  if (accounts === 'failed') {
    return (
      <Step
        icon={CircleDashed}
        title={title}
        subtitle={fd('step.accounts.unavailable')}
        done={false}
      />
    );
  }
  if (accounts.accounts === 0) {
    return (
      <Step icon={CircleDashed} title={title} subtitle={fd('step.accounts.none')} done={false} />
    );
  }
  return (
    <Step
      icon={Check}
      title={title}
      subtitle={fdPlural('step.accounts.count.other', accounts.accounts, {
        n: count(accounts.accounts, getLocale()),
      })}
      done
    />
  );
}

function ProfileStep(props: { accounts: Pending<AccountsSummary> }): ReactNode {
  const { accounts } = props;
  const title = fd('step.profile.title');
  if (accounts === 'pending') {
    return (
      <Step
        icon={CircleDashed}
        title={title}
        subtitle={<Loading className="h-4 w-24" />}
        done={false}
      />
    );
  }
  if (accounts === 'failed' || accounts.accounts === 0) {
    return (
      <Step icon={CircleDashed} title={title} subtitle={fd('step.profile.none')} done={false} />
    );
  }
  const subtitle =
    accounts.profiles === 0
      ? fd('step.profile.own')
      : accounts.profiles === 1 && accounts.sharedProfileName !== null
        ? fd('step.profile.shared', { name: accounts.sharedProfileName })
        : fdPlural('step.profile.count.other', accounts.profiles, {
            n: count(accounts.profiles, getLocale()),
          });
  return <Step icon={Check} title={title} subtitle={subtitle} done />;
}

/** A sentence, not a figure: `StatCard` sets its value in the numeric face. */
function Waiting(): ReactNode {
  return <span className="font-sans text-body text-muted-foreground">{fd('figure.waiting')}</span>;
}

function Credits(props: { credits: Pending<number | null> }): ReactNode {
  const { credits } = props;
  if (credits === 'pending') return <Loading className="h-8 w-20" />;
  if (credits === 'failed' || credits === null) {
    return <Absent reason={{ text: fd('figure.unavailable') }} />;
  }
  return <>{count(credits, getLocale())}</>;
}

function LinkRow(props: { to: string; title: string; subtitle: string }): ReactNode {
  return (
    <Item asChild variant="outline">
      <Link to={props.to}>
        <ItemContent>
          <ItemTitle>{props.title}</ItemTitle>
          <ItemDescription>{props.subtitle}</ItemDescription>
        </ItemContent>
        <ItemActions>
          <ChevronRight aria-hidden className="size-4" />
        </ItemActions>
      </Link>
    </Item>
  );
}

export function FirstDataView(props: FirstDataProps): ReactNode {
  const accountsHref = accountsPath(props.campaignId);

  return (
    <Page>
      <Card className="bg-primary-quiet text-primary-quiet-foreground">
        <CardContent className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="flex flex-col gap-1">
            <h1 className="text-title-lg">{fd('heading', { name: props.campaignName })}</h1>
            <p className="text-body max-w-[66ch]">{fd('description')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button asChild variant="default">
              <Link to={accountsHref}>{fd('action.accounts')}</Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      <ul aria-label={fd('steps.label')} className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <AccountsStep accounts={props.accounts} />
        <ProfileStep accounts={props.accounts} />
        <Step
          icon={Clock}
          title={fd('step.reading.title')}
          subtitle={fd('step.reading.subtitle')}
          done={false}
        />
      </ul>

      <StatGrid>
        <StatCard label={fd('figure.posts')} value={<Waiting />} />
        <StatCard label={fd('figure.reach')} value={<Waiting />} />
        <StatCard label={fd('figure.comments')} value={<Waiting />} />
        <StatCard label={fd('figure.credits')} value={<Credits credits={props.creditsPerDay} />} />
      </StatGrid>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <h2 className="text-title-sm">{fd('posts.title')}</h2>
          </CardHeader>
          <CardContent>
            <EmptyState
              icon={Newspaper}
              title={fd('posts.empty')}
              action={
                props.canWrite ? (
                  <Button asChild variant="outline">
                    <Link to={accountsHref}>{fd('posts.action')}</Link>
                  </Button>
                ) : undefined
              }
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h2 className="text-title-sm">{fd('meanwhile.title')}</h2>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            {props.canWrite && (
              <LinkRow
                to={accountsHref}
                title={fd('meanwhile.accounts.title')}
                subtitle={fd('meanwhile.accounts.subtitle')}
              />
            )}
            <LinkRow
              to={cataloguePath(props.tenantId)}
              title={fd('meanwhile.profile.title')}
              subtitle={fd('meanwhile.profile.subtitle')}
            />
            {props.canWrite && (
              <LinkRow
                to={usersPath(props.tenantId)}
                title={fd('meanwhile.team.title')}
                subtitle={fd('meanwhile.team.subtitle')}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </Page>
  );
}
